import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { formatDate } from '../../../utils/dateHelpers';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { apiString, appointmentTypeEnums, meansOfEngagementEnums, parentUrl } from '../../../utils/constants';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import axios from 'axios';

const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
};

const MemberInfoPanel = () => {
    const { member, associatedEvent, selectedCallItem } = useSelector((state: RootState) => state.officeSchedulerSlice);
    const [mainAddress, setMainAddress] = useState<string>('Loading...');
    const [careTeamName, setCareTeamName] = useState<string>('Loading...');

    useEffect(() => {
        const fetchDefaultAddress = async () => {
            if (member?.Id) {
                try {
                    const response = await axios.get<{ fullAddress: string }>(`${apiString}/Scheduling/getappointmentdefaultaddress`, {
                        params: { memberId: member.Id }
                    });
                    setMainAddress(response.data?.fullAddress || 'Not provided');
                } catch (error) {
                    console.error('Error fetching default address:', error);
                    setMainAddress('Not available');
                }
            }
        };
        fetchDefaultAddress();
    }, [member?.Id]);

    useEffect(() => {
        const fetchCareTeam = async () => {
            if (member?.Id) {
                try {
                    const response = await axios.get(`${apiString}/CareTeams/getmembercareteam`, {
                        params: { memberId: member.Id }
                    });
                    const teamName = response.data?.Name;
                    setCareTeamName(teamName || 'Not Assigned');
                } catch (error) {
                    console.error('Error fetching care team:', error);
                    setCareTeamName('Not Available');
                }
            }
        };
        fetchCareTeam();
    }, [member?.Id]);

    const dob = useMemo(() => {
        if (!member?.DOB) return 'Not Available';
        return `${formatDate(new Date(member.DOB), 'MM-dd-yyyy')} (Age: ${member.Age})`;
    }, [member?.DOB, member?.Age]);

    const appointmentDateTime = useMemo(() => {
        if (!associatedEvent?.AppointmentDateTime) return 'Not Available';
        return formatDate(new Date(associatedEvent.AppointmentDateTime), 'MM/dd/yyyy h:mm aa');
    }, [associatedEvent?.AppointmentDateTime]);

    const appointmentAddress = useMemo(() => {
        if (!associatedEvent) return 'Not Provided';
        return associatedEvent.IsCustomAddress ? associatedEvent.CustomAddress : associatedEvent.address;
    }, [associatedEvent]);

    const providerNames = useMemo(() => {
        if (associatedEvent?.providerNames && associatedEvent.providerNames.length > 0) {
            return associatedEvent.providerNames.join(', ');
        }
        if (associatedEvent?.ProviderName) {
            return associatedEvent.ProviderName;
        }
        if (selectedCallItem?.ProviderNames && selectedCallItem.ProviderNames.length > 0) {
            return selectedCallItem.ProviderNames.join(', ');
        }
        return 'Not Assigned';
    }, [associatedEvent, selectedCallItem]);

    const language = useMemo(() => {
        if (!member) return null;
        const primLang = member.PrimaryLanguage as any;
        if (typeof primLang === 'string' && primLang.trim().length > 0) {
            const val = primLang.trim();
            return val.toLowerCase() !== 'not set' && val.toLowerCase() !== 'unknown' ? val : null;
        }
        if (primLang?.Name && typeof primLang.Name === 'string' && primLang.Name.trim().length > 0) {
            const val = primLang.Name.trim();
            return val.toLowerCase() !== 'not set' && val.toLowerCase() !== 'unknown' ? val : null;
        }
        return null;
    }, [member]);

    const pronouns = useMemo(() => {
        if (!member) return null;
        const anyMember = member as any;
        const pronounVal = anyMember.PreferredPronouns ?? anyMember.Pronouns;
        if (!pronounVal || pronounVal === 0 || pronounVal === '0') {
            if (anyMember.PreferredPronounsOther && anyMember.PreferredPronounsOther.trim().length > 0) {
                return anyMember.PreferredPronounsOther.trim();
            }
            return null;
        }
        if (pronounVal === 1 || pronounVal === '1' || pronounVal === 'HeHim' || pronounVal === 'He/Him') return 'He/Him';
        if (pronounVal === 2 || pronounVal === '2' || pronounVal === 'SheHer' || pronounVal === 'She/Her') return 'She/Her';
        if (pronounVal === 3 || pronounVal === '3' || pronounVal === 'TheyThem' || pronounVal === 'They/Them') return 'They/Them';
        if (pronounVal === 4 || pronounVal === '4' || pronounVal === 'Other') {
            return anyMember.PreferredPronounsOther?.trim() || 'Other';
        }
        if (typeof pronounVal === 'string' && pronounVal.trim().length > 0) {
            return pronounVal.trim();
        }
        return null;
    }, [member]);

    const appointmentType = useMemo(() => {
        if (!associatedEvent) return 'Not Available';
        const anyEvent = associatedEvent as any;
        const typeVal = anyEvent.AppointmentType ?? anyEvent.appointmentType;
        if (typeVal !== undefined && typeVal !== null) {
            const found = appointmentTypeEnums.find((x) => x.value === Number(typeVal));
            if (found) return found.text;
            if (typeof typeVal === 'string' && isNaN(Number(typeVal))) return typeVal;
        }
        return 'Not Available';
    }, [associatedEvent]);

    const meansOfEngagement = useMemo(() => {
        if (!associatedEvent) return 'Not Available';
        const anyEvent = associatedEvent as any;
        const meansVal = anyEvent.meansOfEngagement ?? anyEvent.MeansOfEngagement;
        if (meansVal !== undefined && meansVal !== null) {
            const found = meansOfEngagementEnums.find((x) => x.value === Number(meansVal));
            if (found) return found.text;
            switch (Number(meansVal)) {
                case 1:
                    return 'In-Person/Home';
                case 2:
                    return 'Phone';
                case 3:
                    return 'Telemedicine';
                case 4:
                    return 'Email';
                case 5:
                    return 'Office';
                case 6:
                    return 'Care Conference';
                case 7:
                    return 'Care Coordination';
                case 8:
                    return 'Text Message';
                default:
                    if (typeof meansVal === 'string' && isNaN(Number(meansVal))) return meansVal;
                    break;
            }
        }
        return 'Not Available';
    }, [associatedEvent]);

    if (member === null || associatedEvent === null) return null;

    let memberStatus = 'Unknown';
    if (member.Status) {
        const statusValue = typeof member.Status === 'string' ? parseInt(member.Status) : member.Status;
        if (!isNaN(statusValue)) {
            memberStatus = MemberStatusEnum[statusValue] || member.Status;
        } else {
            memberStatus = member.Status;
        }
    }

    const eligibility = selectedCallItem?.Eligibility || 'Not Available';
    const healthPlan = selectedCallItem?.HealthPlan || 'Not Available';
    const populationName = selectedCallItem?.PopulationName || 'Not Available';

    return (
        <Stack spacing={2} style={{ marginTop: 20 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Member Information</Typography>
                <Button
                    startIcon={<AssignmentIndIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        window.open(`${parentUrl}provider/members-list/member-chart/${member.Id}/member-profile`, '_blank');
                    }}
                >
                    View Chart
                </Button>
            </Stack>
            <Grid container spacing={1}>
                {/* Column 1: Member Demographics */}
                <Grid p={1} size={{ xs: 12, sm: 6, md: 4 }} borderRight={1} borderColor={'gray'}>
                    {member.Mem_AHCCCS_ID && (
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={1} borderBottom={1} alignItems={'center'}>
                            <Typography variant="body2">AHCCCS ID:</Typography>
                            <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                                <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                    {member.Mem_AHCCCS_ID}
                                </Typography>
                                <Tooltip title="Copy to Clipboard" arrow placement="top">
                                    <IconButton size="small" onClick={() => copyToClipboard(member.Mem_AHCCCS_ID)} sx={{ padding: '2px' }}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    )}
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2">Last Name:</Typography>
                        <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                            <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                {member.LastName}
                            </Typography>
                            <Tooltip title="Copy to Clipboard" arrow placement="top">
                                <IconButton size="small" onClick={() => copyToClipboard(member.LastName)} sx={{ padding: '2px' }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2">First Name:</Typography>
                        <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                            <Typography textAlign={'right'} fontWeight={700} variant="body2">
                                {member.FirstName}
                            </Typography>
                            <Tooltip title="Copy to Clipboard" arrow placement="top">
                                <IconButton size="small" onClick={() => copyToClipboard(member.FirstName)} sx={{ padding: '2px' }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2">DOB:</Typography>
                        <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                            <Typography fontWeight={700} textAlign={'right'} variant="body2">
                                {dob}
                            </Typography>
                            <Tooltip title="Copy to Clipboard" arrow placement="top">
                                <IconButton size="small" onClick={() => copyToClipboard(dob)} sx={{ padding: '2px' }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    {pronouns && (
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                            <Typography variant="body2">Pronouns:</Typography>
                            <Typography fontWeight={700} textAlign={'right'} variant="body2">
                                {pronouns}
                            </Typography>
                        </Stack>
                    )}
                    {language && (
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                            <Typography variant="body2">Language:</Typography>
                            <Typography fontWeight={700} textAlign={'right'} variant="body2">
                                {language}
                            </Typography>
                        </Stack>
                    )}
                </Grid>

                {/* Column 2: Appointment Information */}
                <Grid p={1} size={{ xs: 12, sm: 6, md: 4 }} borderRight={1} borderColor={'gray'}>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Provider:
                        </Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {providerNames}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Appointment Time:
                        </Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {appointmentDateTime}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Appointment Type:
                        </Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {appointmentType}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Means of Engagement:
                        </Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {meansOfEngagement}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2" marginRight={'auto'}>
                            Main Address:
                        </Typography>
                        <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                            <Typography fontWeight={700} textAlign={'right'} variant="body2">
                                {mainAddress}
                            </Typography>
                            <Tooltip title="Copy to Clipboard" arrow placement="top">
                                <IconButton size="small" onClick={() => copyToClipboard(mainAddress)} sx={{ padding: '2px' }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    {associatedEvent.IsCustomAddress && (
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                            <Typography variant="body2" marginRight={'auto'}>
                                Appointment Address:
                            </Typography>
                            <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                                <Typography fontWeight={700} textAlign={'right'} variant="body2">
                                    {appointmentAddress}
                                </Typography>
                                <Tooltip title="Copy to Clipboard" arrow placement="top">
                                    <IconButton size="small" onClick={() => copyToClipboard(appointmentAddress)} sx={{ padding: '2px' }}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    )}
                </Grid>

                {/* Column 3: Member Status & Care Team */}
                <Grid p={1} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2">Status:</Typography>
                        <Typography textAlign={'right'} fontWeight={700} variant="body2">
                            {memberStatus}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1}>
                        <Typography variant="body2" marginRight={'auto'}>Care Team:</Typography>
                        <Typography fontWeight={700} variant="body2" textAlign={'right'}>{careTeamName}</Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2">Health Plan:</Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {healthPlan}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2">Population:</Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {populationName}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1} alignItems={'center'}>
                        <Typography variant="body2">Eligibility:</Typography>
                        <Typography fontWeight={700} textAlign={'right'} variant="body2">
                            {eligibility}
                        </Typography>
                    </Stack>
                    {member.IsInTransition && (
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={1} marginTop={1} borderBottom={1}>
                            <Typography variant="body2" color="error" fontWeight={700}>
                                In Transitional Care
                            </Typography>
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </Stack>
    );
};

export default MemberInfoPanel;
